import { ref } from 'vue';

// One SSE client for the unified card chat (`/api/chat/card`). Used for both the
// post-grade reflection (pass `grade`) and the gradeless "discuss / edit this card"
// thread (omit `grade`). Replaces the old review/discussion/flashcard chat trio.

export interface CardToolUse {
  id: string;
  name: string;
  status: 'running' | 'done' | 'error';
  result?: {
    question?: string;
    answer?: string;
    goal?: string | null;
    questionChanged?: boolean;
    answerChanged?: boolean;
    archivedAt?: string;
  } | null;
}

export interface CardChatMessage {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  toolUses?: CardToolUse[];
}

export interface CardChatGrade {
  rating: number;
  aiRating?: number;
  learnerResponse?: string;
  graderNotes?: string | null;
}

export interface CardUpdate {
  question: string;
  answer: string;
  questionChanged: boolean;
  answerChanged: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export function useCardChat() {
  const messages = ref<CardChatMessage[]>([]);
  const isStreaming = ref(false);
  const error = ref<string | null>(null);
  const cardUpdate = ref<CardUpdate | null>(null);
  const archived = ref(false);
  const advanceRequested = ref(false);

  let pendingText = '';
  let streamDone = false;
  let rafId: number | null = null;

  function revealRate(depth: number, done: boolean): number {
    let n = 1;
    if (depth > 200) n = 10;
    else if (depth > 100) n = 5;
    else if (depth > 50) n = 3;
    else if (depth > 20) n = 2;
    if (done) n = Math.max(n, Math.ceil(depth / 30));
    return n;
  }

  function tick() {
    rafId = null;
    if (pendingText.length === 0) {
      if (!streamDone) rafId = requestAnimationFrame(tick);
      return;
    }
    const msg = messages.value[messages.value.length - 1];
    if (!msg || msg.role !== 'assistant') {
      rafId = requestAnimationFrame(tick);
      return;
    }
    const n = revealRate(pendingText.length, streamDone);
    msg.content += pendingText.slice(0, n);
    pendingText = pendingText.slice(n);
    rafId = requestAnimationFrame(tick);
  }

  function ensureTicking() {
    if (rafId === null) rafId = requestAnimationFrame(tick);
  }

  function stopTicking() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function flushPendingTo(msg: CardChatMessage | undefined) {
    if (msg && msg.role === 'assistant' && pendingText.length > 0) {
      msg.content += pendingText;
    }
    pendingText = '';
  }

  async function waitForDrain() {
    while (pendingText.length > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 16));
    }
  }

  function reset() {
    messages.value = [];
    isStreaming.value = false;
    error.value = null;
    cardUpdate.value = null;
    archived.value = false;
    advanceRequested.value = false;
    pendingText = '';
    streamDone = false;
    stopTicking();
  }

  async function sendMessage(
    sessionId: string,
    cardId: string,
    userMessage?: string,
    grade?: CardChatGrade
  ) {
    const trimmed = userMessage?.trim() ?? '';
    if (isStreaming.value) return;
    if (userMessage !== undefined && !trimmed) return;

    error.value = null;

    // Snapshot the on-screen conversation — exactly the context the model gets
    // ("context == screen"). Empty-content turns (tool-only assistant turns) dropped.
    const conversation = messages.value
      .filter((m) => m.content.trim().length > 0)
      .map((m) => ({ role: m.role, content: m.content }));
    if (trimmed) {
      conversation.push({ role: 'user', content: userMessage! });
      messages.value.push({ role: 'user', content: userMessage! });
    }
    isStreaming.value = true;

    messages.value.push({
      role: 'assistant',
      content: '',
      isStreaming: true,
      toolUses: [],
    });
    let assistantMsg = messages.value[messages.value.length - 1]!;
    let toolUseEnded = false;
    pendingText = '';
    streamDone = false;
    ensureTicking();

    try {
      const response = await fetch(`${API_URL}/api/chat/card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          cardId,
          messages: conversation,
          grade,
        }),
      });

      if (!response.ok) {
        throw new Error(
          response.status === 401 ? 'Not authenticated' : `HTTP ${response.status}`
        );
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let done = false;
      while (!done) {
        const result = await reader.read();
        if (result.done) {
          done = true;
          break;
        }

        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ') && currentEvent) {
            const data = JSON.parse(line.slice(6));
            switch (currentEvent) {
              case 'text_delta':
                if (
                  toolUseEnded &&
                  (assistantMsg.content || pendingText.length > 0 || assistantMsg.toolUses?.length)
                ) {
                  flushPendingTo(assistantMsg);
                  assistantMsg.isStreaming = false;
                  messages.value.push({
                    role: 'assistant',
                    content: '',
                    isStreaming: true,
                    toolUses: [],
                  });
                  assistantMsg = messages.value[messages.value.length - 1]!;
                }
                toolUseEnded = false;
                pendingText += data.text;
                ensureTicking();
                break;
              case 'tool_use_start':
                if (!assistantMsg.toolUses) assistantMsg.toolUses = [];
                assistantMsg.toolUses.push({
                  id: data.toolUseId,
                  name: data.name,
                  status: 'running',
                  result: null,
                });
                break;
              case 'tool_use_end': {
                const tu = assistantMsg.toolUses?.find((t) => t.id === data.toolUseId);
                if (tu) {
                  tu.status = data.ok === false ? 'error' : 'done';
                  tu.result = data.result ?? null;
                }
                toolUseEnded = true;
                break;
              }
              case 'card_updated':
                cardUpdate.value = {
                  question: data.question,
                  answer: data.answer,
                  questionChanged: !!data.questionChanged,
                  answerChanged: !!data.answerChanged,
                };
                break;
              case 'card_archived':
                archived.value = true;
                break;
              case 'advance':
                advanceRequested.value = true;
                break;
              case 'error':
                error.value = data.message;
                break;
            }
            currentEvent = '';
          }
        }
      }

      streamDone = true;
      await waitForDrain();
      assistantMsg.isStreaming = false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connection failed';
      flushPendingTo(assistantMsg);
      assistantMsg.isStreaming = false;
    } finally {
      streamDone = true;
      stopTicking();
      isStreaming.value = false;
    }
  }

  return {
    messages,
    isStreaming,
    error,
    cardUpdate,
    archived,
    advanceRequested,
    sendMessage,
    reset,
  };
}
